import {Component, Input, OnInit, ViewChild, Output, EventEmitter, OnDestroy} from '@angular/core';
import {AppCodeViewComponent} from "../../../../components/code-view/code-view.component";
import {
  MatCell, MatCellDef,
  MatColumnDef,
  MatHeaderCell, MatHeaderCellDef,
  MatHeaderRow, MatHeaderRowDef,
  MatNoDataRow,
  MatRow, MatRowDef,
  MatTable,
  MatTableDataSource
} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {NgClass, NgIf} from "@angular/common";
import {ToastrService} from "ngx-toastr";
import {FileUploadService} from "../../../../services/apps/file/file-upload.service";
import {FileSortField, FileStatus, SortType} from "../file";
import {TablerIconsModule} from "angular-tabler-icons";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatTooltip} from "@angular/material/tooltip";
import {MatOption} from "@angular/material/core";
import {MatFormField, MatLabel, MatSelect} from "@angular/material/select";
import {MatSort, MatSortHeader} from "@angular/material/sort";
import {MatInput} from "@angular/material/input";
import {AuthInfoDTO} from "../../../../../core/dto/AuthInfoDTO";
import {AuthorizationService} from "../../../../../core/authorization/AuthorizationService";
import {LocalizationPipe} from "../../../../pipe/localization-pipe";
import {MatIconButton} from "@angular/material/button";
import {MediaViewerService} from "../../../../services/media-viewer.service";
import {LocalizationService} from "../../../../services/localizationService";
import {MatDialog} from "@angular/material/dialog";
import {DeleteConfirmationDialogComponent, DeleteConfirmationData} from "./delete-confirmation-dialog.component";
import {DateTimezonePipe} from "../../../../pipe/date-timezone.pipe";
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { MarkdownViewerModalComponent, MarkdownViewerData } from "./markdown-viewer-modal.component";
import { QaSimpleModalComponent, QaSimpleModalData } from "./qa-simple-modal.component";

@Component({
  selector: 'app-resource-management',
  imports: [
    AppCodeViewComponent,
    MatCell,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderRow,
    MatPaginator,
    MatRow,
    MatTable,
    TablerIconsModule,
    MatProgressSpinner,
    MatTooltip,
    NgIf,
    MatOption,
    MatSelect,
    MatLabel,
    MatNoDataRow,
    MatSortHeader,
    MatSort,
    MatFormField,
    MatInput,
    LocalizationPipe,
    MatHeaderRowDef,
    MatRowDef,
    MatCellDef,
    MatHeaderCellDef,
    MatIconButton,
    DateTimezonePipe,
    NgClass
  ],
  templateUrl: './resource-management.component.html',
  styleUrl: './resource-management.component.scss'
})
export class ResourceManagementComponent implements OnInit, OnDestroy {

  dataSource = new MatTableDataSource<any>();
  totalElements: number;
  pageSize: number;
  currentPage: number;
  searchKey: string;
  // 'labels',
  displayedColumns = ['status', 'type', 'title', 'description', 'file_metadata', 'creator', 'createdDate', 'updatedDate', 'actions'];
  statusType = Object.keys(FileStatus)
  selectedStatus = FileStatus.ALL
  selectedSortField = FileSortField.ID
  selectedSortType = SortType.DESC
  isDataLoading = false;
  activeUser: AuthInfoDTO;

  selectedFiles: Set<number> = new Set<number>();

  @Input() selectedOrganisationId:number;
  @Input() enableSelection: boolean = false;
  @Output() selectedFilesChange = new EventEmitter<number[]>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private toastService: ToastrService,
    private fileService: FileUploadService,
    private authorizationService: AuthorizationService,
    private dialog: MatDialog,
    private localizationService: LocalizationService,
    private mediaViewerService: MediaViewerService
  ) {
  }

  private refreshInterval: any;
  private refreshIntervalTime = 3000; // 3 seconds
  isRefreshing = false;
  private currentData: any[] = [];

  ngOnInit(): void {
    this.activeUser = this.authorizationService.getActiveUser();
    !this.selectedOrganisationId && (this.selectedOrganisationId = this.activeUser.organisationAccount.id);

    if (this.enableSelection) {
      if (!this.displayedColumns.includes('select')) {
        this.displayedColumns = ['select', ...this.displayedColumns.filter(col => col !== 'select')];
      }
    } else {
      this.displayedColumns = this.displayedColumns.filter(col => col !== 'select');
    }

    // Setup debounced search
    this.searchSubject.pipe(
      debounceTime(500), // Wait 0.5 seconds after the last input
      distinctUntilChanged(), // Only emit if the value has changed
      takeUntil(this.destroy$)
    ).subscribe(searchTerm => {
      this.searchKey = searchTerm.trim();
      this.currentPage = 0; // Sayfa numarasını sıfırla
      this.dataSource = new MatTableDataSource<any>();
      this.setDataSource(this.currentPage, this.pageSize, this.selectedSortField, this.selectedSortType);
    });

    this.setDataSource();
    this.startSoftUpdate();
  }

  ngOnDestroy(): void {
    this.stopSoftUpdate();
    this.destroy$.next();
    this.destroy$.complete();
  }

  applyFilter($event) {
    this.searchSubject.next($event.target.value);
  }

  filterByStatus($event) {
    this.dataSource = new MatTableDataSource<any>();
    this.selectedStatus = FileStatus[$event as keyof typeof FileStatus]
    // Filtre değiştiğinde sayfa numarasını sıfırla
    this.currentPage = 0;
    this.setDataSource(this.currentPage + 1, this.pageSize, this.selectedSortField, this.selectedSortType);
  }

  setDataSource(page: number = 1, pageSize: number = 10, sortField: FileSortField = FileSortField.ID, sortType: SortType = SortType.DESC) {
    this.isDataLoading = true
    const data = {
      "query": this.searchKey || "",
      "status": this.selectedStatus === FileStatus.ALL ? "" : this.selectedStatus,
      "organisation": this.selectedOrganisationId
    }



    this.fileService.getFilesInformation(this.selectedOrganisationId, data, page, pageSize, sortField, sortType).subscribe({
      next: (res: any) => {
        // Backend'den gelen veriler zaten doğru sıralanmış, direkt kullan
        this.dataSource.data = res.data.content;
        this.totalElements = res.data.totalElements;
        this.currentPage = res.data.currentPage;
        this.pageSize = pageSize; // Sayfa boyutunu güncelle
        this.isDataLoading = false
        this.currentData = [...this.dataSource.data];

        // TODO : Sıralamayı karıştırdığı için kaldırıldı herhangi bir sorun olursa kontrol edilmeli !
        // setTimeout(() => {
        //   this.dataSource.sort = this.sort;
        // });
      },
      error: error => {
        this.toastService.error("An error occurred while fetching the file status.")
        this.isDataLoading = false
      }
    })
  }

  sortPage($event) {
    this.dataSource = new MatTableDataSource<any>();
    this.selectedSortType = $event.direction === "asc" ? SortType.ASC : SortType.DESC

    // Sıralama alanını belirle
    switch ($event.active) {
      case "createdDate":
        this.selectedSortField = FileSortField.CREATED_DATE;
        break;
      case "updatedDate":
        this.selectedSortField = FileSortField.UPDATED_DATE;
        break;
      case "status":
        this.selectedSortField = FileSortField.STATUS;
        break;
      default:
        this.selectedSortField = FileSortField.ID;
        break;
    }

    // Mevcut filtreleri koruyarak sıralama yap
    this.setDataSource(this.currentPage + 1, this.pageSize, this.selectedSortField, this.selectedSortType);
  }

  onPageChange(event: any) {
    this.dataSource = new MatTableDataSource<any>();
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.setDataSource(this.currentPage + 1, this.pageSize, this.selectedSortField, this.selectedSortType);
  }

  goToFile(element: any) {
    if (element.metadata?.scraper_url) {
      window.open(element.metadata.scraper_url, '_blank', 'noopener,noreferrer');
    }
  }

  viewFile(element: any) {
    if (element.status !== 'FINISHED') {
      this.toastService.warning('Dosya henüz hazır değil. Lütfen işlem tamamlandıktan sonra tekrar deneyin.');
      return;
    }

    const fullFileName = element.metadata?.file_name || element.metadata?.requested_file_name || element.metadata?.name || 'unknown';
    const s3Key = element.s3Key || null;
    const displayName = element.metadata?.requested_file_name || element.metadata?.name || fullFileName;

    let fileExtension = '';
    if (s3Key && s3Key.includes('.')) {
      const nameParts = s3Key.split('.');
      fileExtension = '.' + nameParts[nameParts.length - 1].toLowerCase();
    } else if (element.metadata?.file_content_type || element.metadata?.mime_type) {
      const mimeType = element.metadata.file_content_type || element.metadata.mime_type;
      switch (mimeType) {
        case 'application/msword':
          fileExtension = '.doc';
          break;
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          fileExtension = '.docx';
          break;
        case 'application/vnd.ms-excel':
          fileExtension = '.xls';
          break;
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          fileExtension = '.xlsx';
          break;
        case 'application/pdf':
          fileExtension = '.pdf';
          break;
        case 'text/plain':
          fileExtension = '.txt';
          break;
        case 'image/jpeg':
          fileExtension = '.jpg';
          break;
        case 'image/png':
          fileExtension = '.png';
          break;
        case 'video/mp4':
          fileExtension = '.mp4';
          break;
        default:
          fileExtension = '';
      }
    }

    if (!fileExtension) {
      this.toastService.warning('Dosya uzantısı belirlenemedi. Dosya görüntülenemiyor.');
      return;
    }

    if (!this.mediaViewerService.isFileSupported('dummy' + fileExtension)) {
      this.toastService.warning(`Bu dosya türü (${fileExtension}) desteklenmiyor. Desteklenen türler: ${this.mediaViewerService.getSupportedExtensions().join(', ')}`);
      return;
    }

    // Önce token al, sonra media viewer'ı aç
    this.fileService.getFileToken(this.selectedOrganisationId, element.s3Key).subscribe({
      next: (tokenResponse: any) => {
        // Token ile media viewer'ı aç - viewFile response'ı içeride kullanılacak
        this.mediaViewerService.openFile(this.selectedOrganisationId, tokenResponse, fullFileName);
      },
      error: err => {
        console.error('Token alma hatası:', err);
        console.error('Token error details:', JSON.stringify(err, null, 2));
        this.toastService.error('Dosya erişim token\'ı alınamadı.');
      }
    });
  }

  viewMarkdownFile(element: any) {
    if (element.status !== 'FINISHED') {
      this.toastService.warning('Dosya henüz hazır değil. Lütfen işlem tamamlandıktan sonra tekrar deneyin.');
      return;
    }

    const fileName = element.metadata?.requested_file_name || element.metadata?.name || element.metadata?.file_name || element.metadata?.scraper_url || 'Bilinmeyen Dosya';
    const fileUrl = element.metadata?.scraper_url || null;

    const dialogRef = this.dialog.open(MarkdownViewerModalComponent, {
      width: '90vw',
      height: '90vh',
      maxWidth: '1200px',
      maxHeight: '90vh',
      panelClass: 'markdown-modal-panel',
      data: {
        organisationId: this.selectedOrganisationId,
        fileId: element.id,
        fileName: fileName,
        fileUrl: fileUrl
      } as MarkdownViewerData,
      disableClose: false
    });
  }

  viewQaDetails(element: any) {
    if (element.status !== 'FINISHED') {
      this.toastService.warning('Dosya henüz hazır değil. Lütfen işlem tamamlandıktan sonra tekrar deneyin.');
      return;
    }

    const qaData = element.metadata?.qaData;
    if (!qaData) {
      this.toastService.warning('QA verisi bulunamadı.');
      return;
    }

    const fileName = element.metadata?.requested_file_name || element.metadata?.name || element.metadata?.file_name || 'QA Dosyası';

    const dialogRef = this.dialog.open(QaSimpleModalComponent, {
      width: '600px',
      maxHeight: '80vh',
      data: {
        question: qaData.question || 'Soru bulunamadı',
        answer: qaData.answer || 'Cevap bulunamadı',
        tags: qaData.tags || [],
        fileName: fileName
      } as QaSimpleModalData,
      disableClose: false
    });
  }

  deleteFile(element: any) {
    if (element.status === 'PENDING') {
      this.toastService.warning('Dosya henüz hazır değil. Lütfen işlem tamamlandıktan sonra tekrar deneyin.');
      return;
    }

    const fileName = element.metadata?.requested_file_name || element.metadata?.name || element.metadata?.file_name || element.metadata?.scraper_url || 'Bilinmeyen Dosya';

    const dialogRef = this.dialog.open(DeleteConfirmationDialogComponent, {
      width: '400px',
      data: { fileName: fileName,modalTitle:this.localizationService.getLocalizationValue("ResourceManagement.DeleteConfirmation.Title"),modalQuestion:this.localizationService.getLocalizationValue("ResourceManagement.DeleteConfirmation.Message") } as DeleteConfirmationData,
      disableClose: true // Dialog'un dışına tıklayarak kapatılmasını engelle
    });

    // Dialog component'ine referans al
    const dialogComponent = dialogRef.componentInstance;

    // Dialog'dan gelen deleteConfirmed event'ini dinle
    dialogComponent.deleteConfirmed.subscribe(() => {
      this.toastService.info('Dosyanız siliniyor...', 'Lütfen bekleyiniz.');

      this.fileService.removeFile(this.selectedOrganisationId, element.s3Key, element.id).subscribe({
        next: (response: any) => {
          // Mevcut sayfa ve filtreleri koruyarak listeyi güncelle
          // Eğer mevcut sayfada sadece bir dosya varsa ve o silinirse, önceki sayfaya git
          let targetPage = this.currentPage + 1;
          if (this.dataSource.data.length === 1 && this.currentPage > 0) {
            targetPage = this.currentPage; // Önceki sayfaya git
            this.currentPage = this.currentPage - 1;
          }

          this.setDataSource(targetPage, this.pageSize, this.selectedSortField, this.selectedSortType);
          this.toastService.success("Dosya başarılı bir şekilde silindi.");
          // Başarı mesajından sonra dialog'u kapat
          setTimeout(() => {
            dialogRef.close();
          }, 1000); // 1 saniye bekle
        },
        error: (error) => {
          console.error('Dosya silme hatası:', error);
          this.toastService.error('Dosya silinemedi.', 'Lütfen tekrar deneyiniz.');
          // Hata mesajından sonra dialog'u kapat
          setTimeout(() => {
            dialogRef.close();
          }, 1000); // 1 saniye bekle
        }
      });
    });
  }

  toggleFileSelection(fileId: number): void {
    if (this.selectedFiles.has(fileId)) {
      this.selectedFiles.delete(fileId);
    } else {
      this.selectedFiles.add(fileId);
    }
    this.selectedFilesChange.emit(Array.from(this.selectedFiles));
  }

  isFileSelected(fileId: number): boolean {
    return this.selectedFiles.has(fileId);
  }

  clearSelection(): void {
    this.selectedFiles.clear();
    this.selectedFilesChange.emit([]);
  }

  removeFileFromSelection(fileId: number): void {
    if (this.selectedFiles.has(fileId)) {
      this.selectedFiles.delete(fileId);
      this.selectedFilesChange.emit(Array.from(this.selectedFiles));
    }
  }

  getFileIcon(element: any){
    const s3Key: string = element?.s3Key;
    const metadata: any = element?.metadata || {};

    if (s3Key) {
      const extension = s3Key.split('.').pop()?.toLowerCase();

      const extensionMap: { [key: string]: string } = {
        pdf: 'file-type-pdf',
        doc: 'file-type-doc',
        docx: 'file-type-doc',
        odt: 'file-type-doc',
        txt: 'file-type-txt',
        md: 'file-type-txt',
        jpeg: 'photo-scan',
        jpg: 'photo-scan',
        png: 'photo-scan',
        gif: 'photo-scan',
        bmp: 'photo-scan',
        tiff: 'photo-scan',
        webp: 'photo-scan',
        ico: 'photo-scan',
        svg: 'photo-scan',
        mp4: 'video',
        avi: 'video',
        mov: 'video',
        wmv: 'video',
        flv: 'video',
        mkv: 'video',
        webm: 'video',
        '3gp': 'video',
        mpeg: 'video',
        m4v: 'video',
        xls: 'file-type-xls',
        xlsx: 'file-type-xls',
        ods: 'file-type-xls',
        csv: 'file-type-xls',
      };

      return extensionMap[extension] || 'file-unknown';
    }

    if (metadata?.scraper_url) {
      return 'link-plus';
    }

    if (metadata?.qaData) {
      return 'help-circle';
    }

    return 'file-unknown';
  }

  getFileTypeName(element: any) {
    const s3Key: string = element?.s3Key;
    const metadata: any = element?.metadata || {};

    if (s3Key) {
      const extension = s3Key.split('.').pop()?.toLowerCase();

      const labelMap: { [key: string]: string } = {
        pdf: 'PDF Dosyası',
        doc: 'Word Belgesi',
        docx: 'Word Belgesi',
        odt: 'OpenDocument Metin',
        txt: 'Metin Dosyası',
        md: 'Markdown Dosyası',
        jpeg: 'Resim Dosyası',
        jpg: 'Resim Dosyası',
        png: 'Resim Dosyası',
        gif: 'Resim Dosyası',
        bmp: 'Resim Dosyası',
        tiff: 'Resim Dosyası',
        webp: 'Resim Dosyası',
        ico: 'Simge Dosyası',
        svg: 'SVG Resim',
        mp4: 'Video Dosyası',
        avi: 'Video Dosyası',
        mov: 'Video Dosyası',
        wmv: 'Video Dosyası',
        flv: 'Video Dosyası',
        mkv: 'Video Dosyası',
        webm: 'Video Dosyası',
        '3gp': 'Video Dosyası',
        mpeg: 'Video Dosyası',
        m4v: 'Video Dosyası',
        xls: 'Excel Dosyası',
        xlsx: 'Excel Dosyası',
        ods: 'OpenDocument Tablo',
        csv: 'CSV Dosyası',
      };

      return labelMap[extension] || 'Bilinmeyen Dosya Türü';
    }

    if (metadata?.scraper_url) {
      return 'Scraper Bağlantısı';
    }

    if (metadata?.qaData) {
      return 'Soru-Cevap Verisi';
    }

    return 'Bilinmeyen Dosya Türü';
  }

  private startSoftUpdate(): void {
    this.refreshInterval = setInterval(() => {
      this.softUpdateData();
    }, this.refreshIntervalTime);
  }

  private stopSoftUpdate(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  private softUpdateData(): void {
    this.isRefreshing = true;
    const data = {
      "query": this.searchKey || "",
      "status": this.selectedStatus === FileStatus.ALL ? "" : this.selectedStatus,
      "organisation": this.selectedOrganisationId
    };

    this.fileService.getFilesInformation(this.selectedOrganisationId, data, this.currentPage + 1, this.pageSize, this.selectedSortField, this.selectedSortType).subscribe({
      next: (res: any) => {
        // Backend'den gelen veriler zaten doğru sıralanmış, direkt kullan
        this.compareAndUpdateData(res.data.content);
        this.totalElements = res.data.totalElements;
        this.isRefreshing = false;
      },
      error: error => {
        this.isRefreshing = false;
      }
    });
  }

  private compareAndUpdateData(newData: any[]): void {
    const currentData = this.dataSource.data;

    // Check the new data
    newData.forEach((newItem, index) => {
      const existingItemIndex = currentData.findIndex(item => item.id === newItem.id);

      if (existingItemIndex === -1) {
        // correct position for new item
        currentData.splice(index, 0, newItem);
      } else {
        // updatete and check if the item has changed
        const existingItem = currentData[existingItemIndex];
        if (this.hasItemChanged(existingItem, newItem)) {
          // update only the new items
          Object.keys(newItem).forEach(key => {
            if (existingItem[key] !== newItem[key]) {
              existingItem[key] = newItem[key];
            }
          });
        }
      }
    });

    const newDataIds = newData.map(item => item.id);
    for (let i = currentData.length - 1; i >= 0; i--) {
      if (!newDataIds.includes(currentData[i].id)) {
        currentData.splice(i, 1);
      }
    }

    // DataSource update
    this.dataSource.data = [...currentData];
    this.currentData = [...currentData];
  }

  private hasItemChanged(oldItem: any, newItem: any): boolean {
    // check important ştems for changes
    const fieldsToCheck = ['status', 'updatedDate', 'metadata'];

    return fieldsToCheck.some(field => {
      if (field === 'metadata') {
        return JSON.stringify(oldItem[field]) !== JSON.stringify(newItem[field]);
      }
      return oldItem[field] !== newItem[field];
    });
  }
}
